import TsSyncSettings from './TsSyncSettings';
import { useTsSyncJobs } from '../hooks/useTsSyncJobs';
import { TS_SYNC_QUERY_KEY } from '../api/tsSyncTypes';
import { JOBS_QUERY_KEY } from '../api/jobs';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isTauriApp, tauriConfig, tauriFileSystem } from '../utils/tauri';
import OpenLogsButton from './OpenLogsButton';
import DesktopServerSettings from './DesktopServerSettings';
import type {
  RemoteImageUploadDirectoryTemplateSource,
  ImportFolder,
  ImportScope,
  RemoteImageUploadPlacement,
} from '../api/types';
import type { DbEntry, DbRegistry } from '../utils/tauri';
import { apiClient } from '../api/client';
import { useAccess } from '../auth/access';
import type { SettingsIntent, SettingsRig } from '../utils/settingsIntent';
import ReviewPreferences from './ReviewPreferences';
import DirectorRigs from './director/DirectorRigs';
import { Database as DatabaseIcon, Download, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import TemplateLibrary from './director/TemplateLibrary';
import './director/DirectorPage.css';
import { useDirectorStatus } from '../hooks/useDirectorStatus';
import CalibrationMatchingSettings from './CalibrationMatchingSettings';
import CalibrationRigSettings from './CalibrationRigSettings';
import StackAutomationSettings from './StackAutomationSettings';
import StackMethodSettings from './StackMethodSettings';
import WorkerShareSettings from './WorkerShareSettings';
import StorageSettings from './StorageSettings';
import BackupSettings from './BackupSettings';
import ExportDefaultsSettings from './ExportDefaultsSettings';
import AstroBinSettings from './AstroBinSettings';
import PixInsightSettings from './PixInsightSettings';
import type { AutoImportSettings, DatabaseSummary, ScanRules } from '../api/types';
import { DEFAULT_AUTOIMPORT_SETTINGS, DEFAULT_SCAN_RULES } from '../api/types';
import { planChanges, scanRuleNotes } from '../utils/importNotes';
import {
  describeImportProgress,
  importFinishedMessage,
  noteImportStarted,
  useImportJob,
} from '../hooks/useImportJob';
import { starMetadataFillEnabled } from '../hooks/useStarMetadataFill';
import QualityBackfillControls from './QualityBackfillControls';
import CalibratedCopiesControls from './CalibratedCopiesControls';
import { PurgeRejectsButton } from './RejectRemovalControls';
import MissingGuidRepair from './MissingGuidRepair';
import AutoImportFields from './AutoImportFields';
import AutoImportSummary from './AutoImportSummary';
import ScanRulesFields from './ScanRulesFields';
import RenamedLightsRepair from './RenamedLightsRepair';
import RemotePeerSync from './RemotePeerSync';
import SchedulerSyncControls from './SchedulerSyncControls';
import RemoteSyncPreviews from './RemoteSyncPreviews';
import SeizaCatalogControls from './SeizaCatalogControls';
import ProcessingSetupsManager from './ProcessingSetupsManager';
import CalibrationLibrarySummary from './CalibrationLibrarySummary';
import AstroBinFilterSummary from './AstroBinFilterSummary';
import UserManagement from './UserManagement';
import ApiTokenManagement from './ApiTokenManagement';
import AgentConnect from './AgentConnect';
import { useMcpUrl } from '../utils/agentSetup';
import './TauriSettings.css';
import PathField from './PathField';
import { describeUnsaved, useUnsavedLabels } from '../hooks/useUnsavedChanges';

/**
 * Settings groups unrelated jobs into named tabs so each stays easy to find.
 */
type SettingsTab = 'databases' | 'catalogs' | 'sync' | 'setups' | 'calibration' | 'stacking' | 'storage' | 'backups' | 'performance' | 'review' | 'rigs' | 'templates' | 'users' | 'agents' | 'server';

/** An intent that only picks a tab. */
type TabIntent = Extract<SettingsIntent, SettingsTab>;
const TAB_INTENTS: readonly TabIntent[] = ['rigs', 'templates', 'stacking', 'storage', 'backups', 'review', 'calibration', 'catalogs'];
function isTabIntent(intent: SettingsIntent | null): intent is TabIntent {
  return TAB_INTENTS.includes(intent as TabIntent);
}

const DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE =
  '%YEAR%/%TARGET%/%NIGHT%/%TYPE%';
const REMOTE_UPLOAD_DIRECTORY_TEMPLATE_PRESETS = [
  '%YEAR%/%TARGET%/%NIGHT%/%TYPE%',
  '%TARGET%/%NIGHT%/%TYPE%',
  '%TARGET%/%DATE%/%TYPE%',
  '%TARGET%/%TYPE%/%FILTER%',
  '%NIGHT%/%TARGET%/%TYPE%',
] as const;

const isRemoteUploadDirectoryTemplatePreset = (value: string) =>
  REMOTE_UPLOAD_DIRECTORY_TEMPLATE_PRESETS.some((preset) => preset === value);

interface TauriSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Form to land on when the modal opens. Lets the overview's empty state
   * send the user straight to "add an existing database" or "build one from
   * image folders" instead of dropping them at the top of the modal.
   */
  initialIntent?: SettingsIntent | null;
  /** With the `calibration` intent: the rig whose own settings to open. */
  initialRig?: SettingsRig | null;
  /** With the `calibration` intent: the setting to bring into view and
   *  focus, the rig's own when a rig is given, else the global one. */
  initialField?: string | null;
}

/**
 * Multi-database settings modal.
 *
 * Lists every configured database and lets the user edit name / image_dirs in
 * place, remove a database, or add a new one. Slug renaming is intentionally
 * not exposed here (breaks every existing bookmark for that DB) — users who
 * really want to rename a slug can hand-edit `config.json`.
 *
 * Works in both Tauri and browser/CLI-server mode:
 * - Tauri mode prefers the in-process commands so add/edit feel native and
 *   file pickers open OS dialogs.
 * - Browser mode falls back to HTTP `POST/PUT/DELETE /api/databases` so the
 *   same UI is usable when the server was launched via `psf-guard server`.
 *   The file pickers degrade to plain text inputs.
 */
export default function TauriSettings({
  isOpen,
  onClose,
  initialIntent = null,
  initialRig = null,
  initialField = null,
}: TauriSettingsProps) {
  const isTauri = isTauriApp();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { data: serverInfo } = useQuery({
    queryKey: ['serverInfo'],
    queryFn: apiClient.getServerInfo,
    staleTime: 5 * 60 * 1000,
  });
  // CRUD requires either Tauri (in-process commands always allowed) or the
  // CLI server having been launched with --allow-database-management. The
  // gate is enforced server-side; we mirror it here to hide UI that would
  // just 403.
  const managementAllowed = isTauri || (serverInfo?.allow_database_management ?? false);
  // What edits a catalog, installs catalogs, syncs, imports from the image
  // folders or moves rejects needs only an editor: the gate guards server
  // paths a client names.
  const canEdit = access.canWrite;
  const [registry, setRegistry] = useState<DbRegistry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Inline edit/add form state.
  const [editingId, setEditingId] = useState<string | null>(null); // null = add, slug = edit
  const [formName, setFormName] = useState('');
  const [formDbPath, setFormDbPath] = useState('');
  const [formImageDirs, setFormImageDirs] = useState<string[]>([]);
  const [formRemoteUploadEnabled, setFormRemoteUploadEnabled] = useState(false);
  const [formRemoteSyncEnabled, setFormRemoteSyncEnabled] = useState(false);
  const [formRemoteUploadDir, setFormRemoteUploadDir] = useState('');
  const [formRemoteUploadPlacement, setFormRemoteUploadPlacement] =
    useState<RemoteImageUploadPlacement>('flat');
  /**
   * Whether uploads follow the layout detected from the catalog ('catalog')
   * or the preset/custom template below ('preset'). Choosing a template is a
   * decision: it replaces the catalog match rather than hiding behind it as
   * the fallback (#399).
   */
  const [formRemoteUploadLayoutChoice, setFormRemoteUploadLayoutChoice] =
    useState<RemoteImageUploadDirectoryTemplateSource>('preset');
  /**
   * Whether the person touched the layout select in this edit. Untouched,
   * the save sends no source: the server then treats the template as the
   * form's default, and a first switch to the catalog tree lets detection
   * decide. Touched, `preset` is a pick that holds.
   */
  const [formRemoteUploadLayoutTouched, setFormRemoteUploadLayoutTouched] =
    useState(false);
  const [formRemoteUploadDirectoryTemplate, setFormRemoteUploadDirectoryTemplate] =
    useState(DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE);
  const [formRemoteUploadCatalogMatch, setFormRemoteUploadCatalogMatch] =
    useState<{ template: string; samples: number } | null>(null);
  const [formRemoteUploadRescanRequested, setFormRemoteUploadRescanRequested] =
    useState(false);
  const [formExportDir, setFormExportDir] = useState('');
  const [formProcessDir, setFormProcessDir] = useState('');
  const [formScanRules, setFormScanRules] = useState<ScanRules>(DEFAULT_SCAN_RULES);
  const [formAutoImport, setFormAutoImport] = useState<AutoImportSettings>(
    DEFAULT_AUTOIMPORT_SETTINGS
  );
  const [formRemoteUploadToken, setFormRemoteUploadToken] = useState('');
  const [formRemoteUploadTokenConfigured, setFormRemoteUploadTokenConfigured] =
    useState(false);
  const [formRemoteUploadTokenRevealed, setFormRemoteUploadTokenRevealed] =
    useState(false);
  const [formRemoteUploadTokenCopyState, setFormRemoteUploadTokenCopyState] =
    useState<'idle' | 'copied' | 'failed'>('idle');
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>(isTabIntent(initialIntent) ? initialIntent : 'databases');
  const director = useDirectorStatus();
  // Forms with edits their Save has not sent: closing asks first, so Done
  // does not drop a rig's new location without a word.
  const unsaved = useUnsavedLabels();
  const [confirmClose, setConfirmClose] = useState(false);
  const requestClose = () => {
    if (unsaved.length > 0) setConfirmClose(true);
    else onClose();
  };
  const planningEnabled = !!director.data?.enabled && director.data.protocol_version === 1;
  // true = "create a brand-new TS database from image folders" flow (no
  // existing .sqlite required; the server bootstraps the full TS schema).
  const [createMode, setCreateMode] = useState(false);
  const [tsImportMethod, setTsImportMethod] = useState<'file' | 'plugin'>('file');
  const [connectImportedSource, setConnectImportedSource] = useState(true);
  const [useCustomCatalogPath, setUseCustomCatalogPath] = useState(false);
  const [importDestination, setImportDestination] = useState('');
  const pluginImport = showAddForm && !editingId && !createMode && tsImportMethod === 'plugin';
  const [createAnalyzeQuality, setCreateAnalyzeQuality] = useState(false);
  const [importAnalyzeQuality, setImportAnalyzeQuality] = useState(false);

  // Slug of the database whose import job we're currently tracking; drives
  // the 1s progress poll + the progress panel at the bottom of the modal.
  const [importDbId, setImportDbId] = useState<string | null>(null);
  const { progress: importProgress, isRunning: importRunning } = useImportJob(importDbId);
  // The footer announces an import when it starts. Announce its end too, or
  // a finished job keeps reading "Importing…" until the page reloads.
  const importWasRunning = useRef(false);
  useEffect(() => {
    if (importWasRunning.current && !importRunning && importDbId) {
      const dbName = registry?.databases.find((entry) => entry.id === importDbId)?.name ?? importDbId;
      const message = importFinishedMessage(importProgress, dbName);
      if (message) setStatusMessage(message);
    }
    importWasRunning.current = importRunning;
  }, [importRunning, importProgress, importDbId, registry]);
  // A running preview survives closing or reloading this page. Keep the
  // destination so its completed dry-run can still show the confirm step.
  const [confirmImport, setConfirmImport] = useState<DbEntry | null>(null);
  const [importScope, setImportScope] = useState<ImportScope>('all');
  const [importSkipProcessed, setImportSkipProcessed] = useState(false);
  const [importAcceptOtherRigs, setImportAcceptOtherRigs] = useState(false);
  const [importFolders, setImportFolders] = useState<ImportFolder[]>([]);
  // Checked folder paths. Exactly the configured roots means "everything",
  // and the request then omits image_dirs entirely.
  const [importSelectedDirs, setImportSelectedDirs] = useState<string[]>([]);
  // The import preview renders under the database it imports into, so a
  // scan from that database's calibration dialog lands where the eye is.
  const importPreviewRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (importDbId) {
      importPreviewRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [importDbId]);


  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      // Prefer the Tauri command (returns the full registry including
      // schema_version and active_db_id); fall back to the HTTP listing
      // which gives us enough to render the UI.
      let reg: DbRegistry | null = null;
      const summaries: DatabaseSummary[] = await apiClient.getDatabases();
      if (isTauri) {
        reg = await tauriConfig.getCurrentConfiguration();
        if (reg) {
          reg = {
            ...reg,
            databases: summaries.map((summary) => {
              const persisted = reg?.databases.find(
                (entry) => entry.id === summary.id
              );
              return {
                ...persisted,
                id: summary.id,
                name: summary.name,
                db_path: summary.database_path,
                ts_sync: summary.ts_sync,
                image_dirs: summary.image_directories,
                export_dir: summary.export_directory,
                process_dir: summary.process_directory,
                autoimport: summary.autoimport,
                scan_rules: summary.scan_rules,
                remote_image_upload: {
                  enabled: summary.remote_image_upload?.enabled ?? false,
                  image_dir: summary.remote_image_upload?.image_directory,
                  placement: summary.remote_image_upload?.placement ?? 'flat',
                  directory_template:
                    summary.remote_image_upload?.directory_template ??
                    DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE,
                  catalog_directory_template:
                    summary.remote_image_upload?.catalog_directory_template,
                  directory_template_source:
                    summary.remote_image_upload?.directory_template_source ?? 'preset',
                  directory_template_samples:
                    summary.remote_image_upload?.directory_template_samples ?? 0,
                  token_configured:
                    summary.remote_image_upload?.token_configured ?? false,
                  sync_enabled:
                    summary.remote_image_upload?.sync_enabled ?? false,
                  clients: summary.remote_image_upload?.clients ?? [],
                },
              };
            }),
          };
        }
      }
      if (!reg) {
        reg = {
          schema_version: 2,
          databases: summaries.map((s) => ({
            id: s.id,
            name: s.name,
            db_path: s.database_path,
            ts_sync: s.ts_sync,
            image_dirs: s.image_directories,
            export_dir: s.export_directory,
            process_dir: s.process_directory,
            autoimport: s.autoimport,
            scan_rules: s.scan_rules,
            remote_image_upload: {
              enabled: s.remote_image_upload?.enabled ?? false,
              image_dir: s.remote_image_upload?.image_directory,
              placement: s.remote_image_upload?.placement ?? 'flat',
              directory_template:
                s.remote_image_upload?.directory_template ??
                DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE,
              catalog_directory_template:
                s.remote_image_upload?.catalog_directory_template,
              directory_template_source:
                s.remote_image_upload?.directory_template_source ?? 'preset',
              directory_template_samples:
                s.remote_image_upload?.directory_template_samples ?? 0,
              token_configured: s.remote_image_upload?.token_configured ?? false,
              sync_enabled: s.remote_image_upload?.sync_enabled ?? false,
              clients: s.remote_image_upload?.clients ?? [],
            },
          })),
        };
      }
      setRegistry(reg);

      // Import jobs live on the server, while importDbId is only view state.
      // Recover a running job when settings opens so progress polling resumes
      // after a page reload. There can be one job per database; this modal
      // shows the first active job in registry order.
      const runningImport = (
        await Promise.all(
          reg.databases.map(async (entry) => {
            try {
              const status = await apiClient.getImportStatus(entry.id);
              return status.progress.running ? entry : null;
            } catch (err) {
              console.warn(`Failed to check import status for ${entry.id}:`, err);
              return null;
            }
          })
        )
      ).find((entry): entry is DbEntry => entry !== null);
      if (runningImport) {
        setImportDbId(runningImport.id);
        setConfirmImport(runningImport);
      }

      // Deliberately no form is forced open on an empty registry. Doing that
      // picked "add an existing N.I.N.A. database" for the user and, because
      // the choice buttons hide behind an open form, removed "build one from
      // image folders" from the first run altogether. The welcome banner
      // offers both instead.
    } catch (err) {
      console.error('Failed to load registry:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isTauri]);

  useEffect(() => {
    if (!isOpen) return;
    reload();
    setStatusMessage('');
  }, [isOpen, reload]);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormDbPath('');
    setFormImageDirs([]);
    setFormRemoteUploadEnabled(false);
    setFormRemoteUploadDir('');
    setFormRemoteUploadPlacement('flat');
    setFormRemoteUploadDirectoryTemplate(
      DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE
    );
    setFormRemoteUploadCatalogMatch(null);
    setFormRemoteUploadLayoutChoice('preset');
    setFormRemoteUploadLayoutTouched(false);
    setFormRemoteUploadRescanRequested(false);
    setFormRemoteUploadToken('');
    setFormRemoteUploadTokenConfigured(false);
    setFormRemoteUploadTokenRevealed(false);
    setFormRemoteUploadTokenCopyState('idle');
    setShowAddForm(false);
    setTsImportMethod('file');
    setConnectImportedSource(true);
    setUseCustomCatalogPath(false);
    setImportDestination('');
    setCreateMode(false);
    setCreateAnalyzeQuality(false);
  };

  const startEdit = (entry: DbEntry) => {
    setEditingId(entry.id);
    // A pairing code is scoped to one database; never show it under another.
    setPairingCode(null);
    setPairingExpiresAt(null);
    setPairingCopyState('idle');
    setFormName(entry.name);
    setFormDbPath(entry.db_path);
    setFormImageDirs(entry.image_dirs);
    setFormExportDir(entry.export_dir ?? '');
    setFormProcessDir(entry.process_dir ?? '');
    setFormAutoImport({ ...DEFAULT_AUTOIMPORT_SETTINGS, ...(entry.autoimport ?? {}) });
    setFormScanRules({ ...DEFAULT_SCAN_RULES, ...(entry.scan_rules ?? {}) });
    setFormRemoteUploadEnabled(entry.remote_image_upload?.enabled ?? false);
    setFormRemoteSyncEnabled(entry.remote_image_upload?.sync_enabled ?? false);
    setFormRemoteUploadDir(
      entry.remote_image_upload?.image_dir ?? entry.image_dirs[0] ?? ''
    );
    setFormRemoteUploadPlacement(
      entry.remote_image_upload?.placement ?? 'flat'
    );
    const directoryTemplate =
      entry.remote_image_upload?.directory_template ??
      DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE;
    setFormRemoteUploadDirectoryTemplate(directoryTemplate);
    setFormRemoteUploadLayoutChoice(
      entry.remote_image_upload?.directory_template_source === 'catalog'
        ? 'catalog'
        : 'preset'
    );
    setFormRemoteUploadLayoutTouched(false);
    setFormRemoteUploadCatalogMatch(
      entry.remote_image_upload?.directory_template_source === 'catalog'
        ? {
            template:
              entry.remote_image_upload.catalog_directory_template ??
              directoryTemplate,
            samples: entry.remote_image_upload.directory_template_samples ?? 0,
          }
        : null
    );
    setFormRemoteUploadRescanRequested(false);
    setFormRemoteUploadToken('');
    setFormRemoteUploadTokenConfigured(
      entry.remote_image_upload?.token_configured ??
        Boolean(entry.remote_image_upload?.token_sha256)
    );
    setFormRemoteUploadTokenRevealed(false);
    setFormRemoteUploadTokenCopyState('idle');
    setShowAddForm(true);
    setTsImportMethod('file');
    setCreateMode(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormDbPath('');
    setFormImageDirs([]);
    setFormRemoteUploadEnabled(false);
    setFormRemoteUploadDir('');
    setFormRemoteUploadPlacement('flat');
    setFormRemoteUploadDirectoryTemplate(
      DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE
    );
    setFormRemoteUploadCatalogMatch(null);
    setFormRemoteUploadLayoutChoice('preset');
    setFormRemoteUploadLayoutTouched(false);
    setFormRemoteUploadRescanRequested(false);
    setFormRemoteUploadToken('');
    setFormRemoteUploadTokenConfigured(false);
    setFormRemoteUploadTokenRevealed(false);
    setFormRemoteUploadTokenCopyState('idle');
    setShowAddForm(true);
    setCreateMode(true);
    setCreateAnalyzeQuality(false);
  };

  const startAdd = async () => {
    setEditingId(null);
    setFormName('');
    setFormImageDirs([]);
    setFormRemoteUploadEnabled(false);
    setFormRemoteUploadDir('');
    setFormRemoteUploadPlacement('flat');
    setFormRemoteUploadDirectoryTemplate(
      DEFAULT_REMOTE_UPLOAD_DIRECTORY_TEMPLATE
    );
    setFormRemoteUploadCatalogMatch(null);
    setFormRemoteUploadLayoutChoice('preset');
    setFormRemoteUploadLayoutTouched(false);
    setFormRemoteUploadRescanRequested(false);
    setFormRemoteUploadToken('');
    setFormRemoteUploadTokenConfigured(false);
    setFormRemoteUploadTokenRevealed(false);
    setFormRemoteUploadTokenCopyState('idle');
    setShowAddForm(true);
    setTsImportMethod('file');
    setCreateMode(false);
    setFormDbPath('');

    if (isTauri) {
      // Try to seed with the default N.I.N.A. database path (Windows only).
      try {
        const def = await tauriFileSystem.getDefaultNinaPath();
        if (def) setFormDbPath(def);
      } catch {
        // Ignore — the form just stays empty.
      }
    }
  };

  // Land on the form the caller asked for: at mount, and again when a panel
  // inside the open modal (Settings › Rigs offers "Add database") asks for
  // another one.
  useEffect(() => {
    if (initialIntent === 'create') { setActiveTab('databases'); startCreate(); }
    if (initialIntent === 'add') { setActiveTab('databases'); void startAdd(); }
    if (isTabIntent(initialIntent)) setActiveTab(initialIntent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIntent]);

  // An image directory typed or picked, added with the Add button.
  const [pendingImageDir, setPendingImageDir] = useState('');
  const handleAddManualImageDir = () => {
    const trimmed = pendingImageDir.trim();
    if (trimmed && !formImageDirs.includes(trimmed)) {
      setFormImageDirs([...formImageDirs, trimmed]);
      setPendingImageDir('');
    }
  };

  const handleRemoveImageDir = (index: number) => {
    const removed = formImageDirs[index];
    const remaining = formImageDirs.filter((_, i) => i !== index);
    setFormImageDirs(remaining);
    if (formRemoteUploadDir === removed) {
      setFormRemoteUploadDir(remaining[0] ?? '');
    }
  };

  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState<number | null>(null);
  const [pairingCopyState, setPairingCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleIssuePairingCode = async () => {
    if (!editingId) return;
    try {
      const issued = await apiClient.issuePairingCode(editingId);
      setPairingCode(issued.pairing_token);
      setPairingExpiresAt(issued.expires_at);
      setPairingCopyState('idle');
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const handleRevokeClient = async (clientUuid: string) => {
    if (!editingId) return;
    try {
      await apiClient.revokePairedClient(editingId, clientUuid);
      // The databases list is component state fed by reload(), not a
      // react-query cache — invalidation alone would leave the revoked
      // client on screen.
      await reload();
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      setStatusMessage('Client revoked');
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const handleCopyPairingCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setPairingCopyState('copied');
    } catch {
      setPairingCopyState('failed');
    }
  };

  const handleGenerateRemoteUploadToken = () => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
    setFormRemoteUploadToken(token);
    setFormRemoteUploadTokenRevealed(true);
    setFormRemoteUploadTokenCopyState('idle');
  };

  const handleCopyRemoteUploadToken = async () => {
    try {
      await navigator.clipboard.writeText(formRemoteUploadToken);
      setFormRemoteUploadTokenCopyState('copied');
    } catch (error) {
      console.error('Failed to copy remote upload token:', error);
      setFormRemoteUploadTokenCopyState('failed');
    }
  };

  const handleSaveForm = async () => {
    const destination = useCustomCatalogPath ? importDestination.trim() : '';
    if (!editingId && useCustomCatalogPath && !/^(?:\/|[a-z]:[\\/]|\\\\[^\\]+\\[^\\]+)/i.test(destination)) {
      setStatusMessage('Enter an absolute destination database path, or turn off Use a custom database path (advanced).');
      return;
    }
    if (createMode || pluginImport) {
      // "New database from images": the server bootstraps a fresh TS-schema
      // database and imports the folders in the background.
      if (formImageDirs.length === 0) {
        setStatusMessage('Add at least one image directory to import');
        return;
      }
      const name = formName.trim() || (pluginImport ? 'Target Scheduler' : 'Imported Images');
      setIsApplying(true);
      setStatusMessage('');
      try {
        const created = await apiClient.createDatabaseFromImages({
          name,
          image_dirs: formImageDirs,
          backfill: !pluginImport && createAnalyzeQuality,
          fill_metadata: starMetadataFillEnabled(),
          ...(destination ? { db_path: destination } : {}),
        });
        queryClient.invalidateQueries({ queryKey: ['databases'] });
        queryClient.invalidateQueries({ queryKey: ['db'] });
        setImportDbId(created.database.id);
        await reload();
        if (pluginImport) {
          startEdit({ id: created.database.id, name: created.database.name,
            db_path: created.database.database_path, image_dirs: created.database.image_directories });
          setStatusMessage('Catalog created. Generate a pairing code below to connect PSF Guard Sync in N.I.N.A.');
        } else {
          resetForm();
          setStatusMessage(`Created ${created.database.name}; importing frames…`);
        }
      } catch (err) {
        console.error('create-from-images failed:', err);
        const msg = err instanceof Error ? err.message : String(err);
        setStatusMessage(`Failed to create: ${msg}`);
      } finally {
        setIsApplying(false);
      }
      return;
    }

    if (!formDbPath.trim()) {
      setStatusMessage('Please select a database file');
      return;
    }

    const inferredName =
      formName.trim() ||
      formDbPath.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, '') ||
      'Database';

    setIsApplying(true);
    setStatusMessage('');

    try {
      // Use HTTP endpoints — they're available in both Tauri and CLI-server
      // mode, and updating live `AppState.databases` rather than waiting for
      // a server restart.
      if (editingId) {
        if (formRemoteUploadEnabled && !formRemoteUploadDir) {
          setStatusMessage('Select an image directory for remote uploads');
          setIsApplying(false);
          return;
        }
        if (
          formRemoteUploadEnabled &&
          formRemoteUploadPlacement === 'target_tree' &&
          formRemoteUploadLayoutChoice === 'preset' &&
          !formRemoteUploadDirectoryTemplate.trim()
        ) {
          setStatusMessage('Enter a folder layout');
          setIsApplying(false);
          return;
        }
        if (
          formRemoteUploadToken.length > 0 &&
          formRemoteUploadToken.length < 24
        ) {
          setStatusMessage('Remote API key must be at least 24 characters');
          setIsApplying(false);
          return;
        }
        if (
          formRemoteUploadEnabled &&
          !formRemoteUploadTokenConfigured &&
          formRemoteUploadToken.length === 0
        ) {
          setStatusMessage('Generate a remote API key before enabling image uploads');
          setIsApplying(false);
          return;
        }
        if (
          formRemoteSyncEnabled &&
          !formRemoteUploadTokenConfigured &&
          formRemoteUploadToken.length === 0
        ) {
          setStatusMessage('Generate a remote API key before enabling scheduler sync');
          setIsApplying(false);
          return;
        }
        await apiClient.updateDatabase(editingId, {
          name: inferredName,
          db_path: formDbPath.trim(),
          image_dirs: formImageDirs,
          export_dir: formExportDir.trim(),
          process_dir: formProcessDir.trim(),
          autoimport: formAutoImport,
          scan_rules: formScanRules,
          remote_image_upload: {
            enabled: formRemoteUploadEnabled,
            image_directory: formRemoteUploadDir || undefined,
            token: formRemoteUploadToken || undefined,
            sync_enabled: formRemoteSyncEnabled,
            placement: formRemoteUploadPlacement,
            // In catalog mode no template is sent: the match stays as it is
            // and the server leaves the stored fallback alone.
            directory_template:
              formRemoteUploadEnabled &&
              formRemoteUploadPlacement === 'target_tree' &&
              formRemoteUploadLayoutChoice === 'preset'
                ? formRemoteUploadDirectoryTemplate.trim()
                : undefined,
            directory_template_source:
              formRemoteUploadEnabled &&
              formRemoteUploadPlacement === 'target_tree' &&
              (formRemoteUploadLayoutTouched ||
                formRemoteUploadLayoutChoice === 'catalog')
                ? formRemoteUploadLayoutChoice
                : undefined,
            rescan_directory_layout:
              formRemoteUploadEnabled &&
              formRemoteUploadPlacement === 'target_tree' &&
              formRemoteUploadRescanRequested
                ? true
                : undefined,
          },
        });
      } else {
        const job = await apiClient.importTsDatabase({
          name: inferredName,
          db_path: formDbPath.trim(),
          image_dirs: formImageDirs,
          ...(!connectImportedSource ? { connect_source: false } : {}),
          ...(destination ? { destination_path: destination } : {}),
        });
        queryClient.setQueryData<import('../api/tsSyncTypes').TsSyncJob[]>(TS_SYNC_QUERY_KEY, old => [...(old ?? []).filter(item => item.id !== job.id), job]);
        void queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEY });
        setStatusMessage(connectImportedSource ? 'Importing Target Scheduler into a PSF Guard catalog…' : 'Importing a standalone catalog copy…');
        return;
      }

      // Invalidate every per-DB query so the merged-overview hooks pull
      // fresh data for the just-added/edited DB.
      queryClient.invalidateQueries({ queryKey: ['databases'] });
      queryClient.invalidateQueries({ queryKey: ['db'] });

      await reload();
      resetForm();
      setStatusMessage('Saved.');
    } catch (err) {
      console.error('save failed:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setStatusMessage(`Failed to save: ${msg}`);
    } finally {
      setIsApplying(false);
    }
  };

  // Import is two-step: a dry-run PREVIEW first (rolled back server-side),
  // then an explicit confirmation. Nothing touches the database until the
  // user has seen exactly what would be attached vs newly created.
  // Scope, folder, and artifact choices as request fields. Selection equal
  // to the configured roots means "everything" and sends no image_dirs, so
  // the default run needs no folder consent beyond what is configured.
  const importOptionsOf = (entry: DbEntry) => {
    const roots = [...entry.image_dirs].sort();
    const selection = [...importSelectedDirs].sort();
    const isDefaultSelection =
      selection.length === roots.length && selection.every((dir, i) => dir === roots[i]);
    return {
      scope: importScope === 'all' ? undefined : importScope,
      skip_processed: importSkipProcessed || undefined,
      accept_other_rigs: importAcceptOtherRigs || undefined,
      image_dirs: isDefaultSelection || selection.length === 0 ? undefined : selection,
    };
  };

  const previewImport = async (entry: DbEntry, options: ReturnType<typeof importOptionsOf>) => {
    const status = await apiClient.startImport(entry.id, {
      dry_run: true,
      backfill: false,
      ...options,
    });
    noteImportStarted(queryClient, entry.id, status);
    setImportDbId(entry.id);
    setConfirmImport(entry);
    setStatusMessage(
      status.started
        ? `Previewing import into ${entry.name}… nothing is written until you confirm.`
        : 'An import is already running for this database.'
    );
  };

  /**
   * Preview an import of the database's folders, or of `folders` when the
   * auto-import summary asks to review frames it held.
   */
  const handleImport = async (entry: DbEntry, folders?: string[]) => {
    if (entry.image_dirs.length === 0) {
      setStatusMessage(
        `"${entry.name}" has no image directories configured — edit it and add the folders to import.`
      );
      return;
    }
    setIsApplying(true);
    setStatusMessage('');
    setImportAnalyzeQuality(false);
    setImportScope('all');
    setImportSkipProcessed(false);
    setImportSelectedDirs(folders?.length ? [...folders] : [...entry.image_dirs]);
    setImportFolders([]);
    apiClient
      .getImportFolders(entry.id)
      .then(setImportFolders)
      .catch(() => setImportFolders([]));
    try {
      await previewImport(entry, {
        scope: undefined,
        skip_processed: undefined,
        accept_other_rigs: undefined,
        image_dirs: folders?.length ? [...folders] : undefined,
      });
    } catch (err) {
      console.error('import preview failed to start:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setStatusMessage(`Failed to preview import: ${msg}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRepreviewImport = async () => {
    if (!confirmImport) return;
    setIsApplying(true);
    try {
      await previewImport(confirmImport, importOptionsOf(confirmImport));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatusMessage(`Failed to preview import: ${msg}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!confirmImport) return;
    const entry = confirmImport;
    setConfirmImport(null);
    setIsApplying(true);
    try {
      const status = await apiClient.startImport(entry.id, {
        dry_run: false,
        backfill: importAnalyzeQuality,
        fill_metadata: starMetadataFillEnabled(),
        ...importOptionsOf(entry),
      });
      noteImportStarted(queryClient, entry.id, status);
      setStatusMessage(`Importing frames into ${entry.name}…`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatusMessage(`Failed to start import: ${msg}`);
    } finally {
      setIsApplying(false);
    }
  };

  const handleRemove = async (entry: DbEntry) => {
    if (!confirm(
      `Remove "${entry.name}" from PSF Guard? Its catalog file and images stay on disk, and you can add it again.`
    )) return;
    setIsApplying(true);
    try {
      const ok = await apiClient.removeDatabase(entry.id);
      if (ok) {
        queryClient.invalidateQueries({ queryKey: ['databases'] });
        queryClient.invalidateQueries({ queryKey: ['db', entry.id] });
        await reload();
        if (editingId === entry.id) resetForm();
        setStatusMessage(`Removed ${entry.name}.`);
      } else {
        setStatusMessage('Remove failed.');
      }
    } catch (err) {
      console.error('remove failed:', err);
      const msg = err instanceof Error ? err.message : String(err);
      setStatusMessage(`Failed to remove: ${msg}`);
    } finally {
      setIsApplying(false);
    }
  };

  // CRUD changes are applied to the live server immediately (HTTP endpoints
  // update both registry file and AppState.databases). The restart button is
  // only useful in rare cases where the live state diverged from disk — keep
  // it as an opt-in escape hatch in Tauri mode.
  const handleRestart = async () => {
    if (!isTauri) {
      setStatusMessage('Refreshing interface...');
      setTimeout(() => window.location.reload(), 800);
      return;
    }
    setIsApplying(true);
    setStatusMessage('Restarting server...');
    try {
      await tauriConfig.restartServer();
      window.location.reload();
    } catch (err) {
      console.error('restart failed:', err);
      setStatusMessage(`Restart failed: ${err}`);
      setIsApplying(false);
    }
  };

  const databases = registry?.databases ?? [];
  const tsJobs = useTsSyncJobs(managementAllowed && canEdit);
  const tsImportRunning = tsJobs.data?.some(job => job.id.startsWith('import:') && job.running) ?? false;
  const observedImports = useRef(new Set<string>());
  useEffect(() => {
    for (const job of tsJobs.data ?? []) {
      if (!job.id.startsWith('import:')) continue;
      const key = `${job.id}:${job.started_at}`;
      if (job.running) {
        observedImports.current.add(key);
      } else if (observedImports.current.delete(key) && job.stage === 'complete' && job.catalog_id) {
        void reload();
        if (showAddForm && !editingId && !createMode && tsImportMethod === 'file') resetForm();
        setStatusMessage(job.source_connected === false
          ? 'Catalog import complete. The source file is not connected for sync.'
          : 'Target Scheduler import complete. Automatic sync and grade writeback are enabled.');
      }
    }
  // reload and resetForm are defined inside the component; job tracking does not depend on their identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tsJobs.data]);

  if (!isOpen) return null;

  const hasDatabases = databases.length > 0;

  // Installing catalogs and syncing take an editor; sync has nothing to
  // talk about until a catalog exists.
  const visibleTabs: Array<{ id: SettingsTab; label: string }> = [
    { id: 'databases', label: 'Databases' },
    ...(isTauri ? ([{ id: 'server', label: 'Server' }] as const) : []),
    ...(canEdit
      ? ([{ id: 'catalogs', label: 'Catalogs' }] as const)
      : []),
    ...(canEdit && hasDatabases
      ? ([{ id: 'sync', label: 'Sync' }] as const)
      : []),
    // Setups are global display parameters, not filesystem paths, so the tab
    // is not management-gated.
    { id: 'setups', label: 'Setups' },
    // Matching rules, master building and which lights stacks keep; shown to
    // everyone, saved only with write access.
    { id: 'calibration', label: 'Calibration' },
    // Server-wide, like the setups, and not management-gated either.
    { id: 'stacking', label: 'Stacking' },
    // Where generated files live and how full their volumes may get; shown
    // to everyone, changes need database management.
    { id: 'storage', label: 'Storage' },
    // An archive holds every catalog and the server's settings, so backups
    // take an editor on a server that allows database management.
    ...(canEdit && managementAllowed
      ? ([{ id: 'backups', label: 'Backups' }] as const)
      : []),
    // How much of the processor stacking, quality scans and previews may use
    // together; server-wide and not management-gated, like Stacking.
    { id: 'performance', label: 'Performance' },
    // Review preferences are stored in this browser, so no gate either.
    { id: 'review', label: 'Review' },
    // Rig setup and the exposure template library are Planning's configuration.
    ...(planningEnabled
      ? ([{ id: 'rigs', label: 'Rigs' }, { id: 'templates', label: 'Exposure templates' }] as const)
      : []),
    ...(!isTauri && access.status.authentication_required
      ? ([{ id: 'users', label: 'Users' }] as const)
      : []),
    // With accounts, agents connect from Users with a token of their own.
    ...(access.status.authentication_required
      ? []
      : ([{ id: 'agents', label: 'Agents' }] as const)),
  ];
  // Derive rather than store: removing the last database takes the Sync tab
  // away, and the selection has to fall back in the same render.
  // A Planning tab asked for before Planning's status arrives waits for it
  // rather than flashing Databases.
  const waitingForPlanning = (activeTab === 'rigs' || activeTab === 'templates') && director.isPending;
  const currentTab = visibleTabs.some((tab) => tab.id === activeTab) || waitingForPlanning
    ? activeTab
    : 'databases';

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const index = visibleTabs.findIndex((tab) => tab.id === currentTab);
    const next = visibleTabs[(index + step + visibleTabs.length) % visibleTabs.length];
    setActiveTab(next.id);
    document.getElementById(`settings-tab-${next.id}`)?.focus();
  };

  const renderDatabaseForm = (entry: DbEntry | null) => {
    const editorHeadingId = entry ? `database-editor-heading-${entry.id}` : undefined;

    return (
      <section
        className={entry ? 'db-row-editor' : 'settings-section catalog-create-form'}
        aria-labelledby={editorHeadingId}
      >
        {entry ? (
          <div className="db-editor-heading">
            <span className="db-editor-context">Editing database</span>
            <h3 id={editorHeadingId}>
              {entry.name} <code className="db-row-slug">{entry.id}</code>
            </h3>
          </div>
        ) : (
          <h3>{createMode ? 'New Database from Images' : !pluginImport && !connectImportedSource ? 'Import a catalog copy' : 'Import from Target Scheduler'}</h3>
        )}

        {!editingId && <>
          <fieldset className="catalog-source-options" disabled={isApplying || tsImportRunning}>
            <legend>Start a catalog</legend>
            <div className="catalog-source-grid">
              <label className="catalog-source-option">
                <input type="radio" name="catalog-source" aria-label="Image folders" aria-checked={createMode} checked={createMode} onChange={() => setCreateMode(true)} />
                <span><strong>Image folders</strong><small>Import FITS or XISF folders into a new catalog.</small></span>
              </label>
              <label className="catalog-source-option">
                <input type="radio" name="catalog-source" aria-label="Import a local TS file" aria-checked={!createMode && tsImportMethod === 'file'} checked={!createMode && tsImportMethod === 'file'} onChange={() => { setCreateMode(false); setTsImportMethod('file'); }} />
                <span><strong>Import a local TS file</strong><small>Copy a TS or PSF Guard database. Choose whether to keep TS connected.</small></span>
              </label>
              <label className="catalog-source-option">
                <input type="radio" name="catalog-source" aria-label="Use the PSF Guard Sync plugin" aria-checked={pluginImport} checked={pluginImport} onChange={() => { setCreateMode(false); setTsImportMethod('plugin'); }} />
                <span><strong>Use the PSF Guard Sync plugin</strong><small>Connect N.I.N.A. over the network. No local TS file required.</small></span>
              </label>
            </div>
          </fieldset>
          {!createMode && <p className="muted">{pluginImport
            ? 'Choose a shared image folder or an empty folder for incoming uploads. Create the catalog, then pair the plugin. The plugin controls its own sync schedule.'
            : 'PSF Guard copies all catalog data, including existing PSF Guard data. Image files stay in their current folders.'}</p>}
        </>}
        {createMode && (
          <p className="muted">
            Builds a PSF Guard catalog from the selected image folders. Each target gets its own project. Nearby,
            similarly dated panels with matching panel names share a
            mosaic project. You can rename or reorganize them afterwards.
            The import reads headers only; pixel-based quality work is a
            separate option below.
          </p>
        )}

        <div className="database-config">
          <label htmlFor="database-display-name">Display name (optional):</label>
          <input
            id="database-display-name"
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={
              createMode
                ? 'e.g. 2026 Archive (defaults to "Imported Images")'
                : 'e.g. Imaging Rig (defaults to filename)'
            }
            className="file-path-input"
          />
        </div>

        {!editingId && <fieldset className="catalog-storage" disabled={isApplying || tsImportRunning}>
          <legend>Catalog storage</legend>
          <p className="muted">By default, PSF Guard stores new catalogs in the databases folder beside its registry.</p>
          <label className="checkbox-label"><input type="checkbox" checked={useCustomCatalogPath} aria-checked={useCustomCatalogPath}
            aria-controls="custom-catalog-location" onChange={event => setUseCustomCatalogPath(event.target.checked)} /> Use a custom database path (advanced)</label>
          {useCustomCatalogPath && <div id="custom-catalog-location" className="database-config">
            <label htmlFor="import-destination-path">Destination database path:</label>
            <input id="import-destination-path" className="file-path-input" type="text" value={importDestination}
              onChange={event => setImportDestination(event.target.value)} placeholder="e.g. /srv/psf-guard/catalog.sqlite" />
            <p className="muted">Use an absolute path to a new file on the machine running PSF Guard. Existing catalogs are not replaced.</p>
          </div>}
        </fieldset>}

        {!createMode && !pluginImport && (
          <div className="database-config">
            <label htmlFor="catalog-source-path">Source database file:</label>
            <div className="file-input-group">
              <PathField
                id="catalog-source-path"
                kind="file"
                disabled={!!editingId && !!databases.find(entry => entry.id === editingId)?.ts_sync}
                pick={tauriFileSystem.pickDatabaseFile}
                value={formDbPath}
                onChange={setFormDbPath}
                placeholder="Select or enter database path"
                className="file-path-input"
              />
            </div>
          </div>
        )}

        {!editingId && !createMode && !pluginImport && <>
          <label className="checkbox-label"><input type="checkbox" checked={connectImportedSource} disabled={isApplying || tsImportRunning}
            onChange={e => setConnectImportedSource(e.target.checked)} /> Keep the source connected for Target Scheduler sync</label>
          <p className="muted">{connectImportedSource
            ? 'Automatic sync imports new captures and writes grade changes back. Director activation sends reviewed planning through this connection.'
            : 'Create a standalone catalog copy. PSF Guard will not sync with or write back to the source file.'}
            {' '}Server settings and saved Director plans require a full backup restore.</p>
        </>}

        <div className="database-config">
          <label>Image Directories:</label>
          <div className="file-input-group">
            <PathField
              dialogTitle="Select Image Directory"
              value={pendingImageDir}
              onChange={setPendingImageDir}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddManualImageDir();
                }
              }}
              placeholder={isTauri ? 'Browse, or type an absolute path, then Add' : 'Type an absolute path and press Add'}
              className="file-path-input"
            />
            <button
              onClick={handleAddManualImageDir}
              className="browse-button"
              disabled={!pendingImageDir.trim()}
            >
              Add
            </button>
          </div>
          {formImageDirs.length > 0 && (
            <div className="image-directories">
              {formImageDirs.map((dir, index) => (
                <div key={dir} className="image-directory-item">
                  <span>📂 {dir}</span>
                  <button
                    onClick={() => handleRemoveImageDir(index)}
                    className="remove-button"
                    title="Remove directory"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {editingId && (
          <div className="database-config">
            <label>Connect PSF Guard Sync in N.I.N.A.:</label>
            <p>Install <strong>PSF Guard Sync</strong> from the N.I.N.A. plugin repository <code>https://nina-plugins.psf-guard.com/</code>, then restart N.I.N.A. Generate a pairing code and enter it with this server’s URL in the plugin. Click <strong>Pair</strong>, then <strong>Test connection</strong>. The plugin controls its own sync schedule. To receive image files too, enable <strong>Accept remote image uploads</strong> below and choose a <strong>Receive directory</strong>.</p>
            <div className="remote-upload-token-row">
              <button
                type="button"
                onClick={handleIssuePairingCode}
                className="browse-button"
              >
                Generate pairing code
              </button>
              {pairingCode && (
                <>
                  <input
                    type="text"
                    readOnly
                    value={pairingCode}
                    className="file-path-input"
                    onFocus={(event) => event.target.select()}
                  />
                  <button
                    type="button"
                    onClick={handleCopyPairingCode}
                    className="browse-button"
                  >
                    {pairingCopyState === 'copied' ? 'Copied' : 'Copy'}
                  </button>
                </>
              )}
            </div>
            {pairingCode && (
              <small className="remote-upload-token-notice" role="status">
                {pairingCopyState === 'failed'
                  ? 'Copy failed. Select and copy the code manually.'
                  : `Paste this code into the client. One use, expires ${
                      pairingExpiresAt
                        ? new Date(pairingExpiresAt * 1000).toLocaleTimeString()
                        : 'in an hour'
                    }. Each pairing adds its own revocable credential.`}
              </small>
            )}
            {(databases.find((db) => db.id === editingId)?.remote_image_upload
              ?.clients?.length ?? 0) > 0 && (
              <div className="paired-clients">
                <label>Paired clients:</label>
                <ul>
                  {databases
                    .find((db) => db.id === editingId)!
                    .remote_image_upload!.clients!.map((client) => (
                      <li key={client.client_uuid}>
                        <span>
                          {client.name} ·{' '}
                          {new Date(client.paired_at * 1000).toLocaleDateString()}
                        </span>
                        <button
                          type="button"
                          className="browse-button"
                          onClick={() => handleRevokeClient(client.client_uuid)}
                        >
                          Revoke
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <label htmlFor="remote-upload-token">Remote API key:</label>
            <div className="remote-upload-token-row">
              <input
                id="remote-upload-token"
                type={formRemoteUploadTokenRevealed ? 'text' : 'password'}
                value={formRemoteUploadToken}
                onChange={(event) => {
                  setFormRemoteUploadToken(event.target.value);
                  setFormRemoteUploadTokenCopyState('idle');
                }}
                placeholder={
                  formRemoteUploadTokenConfigured
                    ? 'Unchanged'
                    : 'At least 24 characters'
                }
                className="file-path-input"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={handleGenerateRemoteUploadToken}
                className="browse-button"
              >
                Generate
              </button>
              {formRemoteUploadTokenRevealed &&
                formRemoteUploadToken.length > 0 && (
                  <button
                    type="button"
                    onClick={handleCopyRemoteUploadToken}
                    className="browse-button"
                  >
                    {formRemoteUploadTokenCopyState === 'copied'
                      ? 'Copied'
                      : 'Copy'}
                  </button>
                )}
            </div>
            {formRemoteUploadTokenRevealed && (
              <small
                className={`remote-upload-token-notice ${
                  formRemoteUploadTokenCopyState === 'failed' ? 'error' : ''
                }`}
                role="status"
              >
                {formRemoteUploadTokenCopyState === 'failed'
                  ? 'Copy failed. Select and copy the key manually.'
                  : 'Copy this key now. It will not be shown again after saving.'}
              </small>
            )}
            <label className="quality-analysis-option">
              <input
                type="checkbox"
                checked={formRemoteSyncEnabled}
                onChange={(event) =>
                  setFormRemoteSyncEnabled(event.target.checked)
                }
              />
              <span>
                <strong>Accept remote scheduler sync</strong>
                <small>
                  Lets a holder of this key merge projects, targets,
                  plans, and grades into this database.
                </small>
              </span>
            </label>
            <label className="quality-analysis-option">
              <input
                type="checkbox"
                checked={formRemoteUploadEnabled}
                onChange={(event) =>
                  setFormRemoteUploadEnabled(event.target.checked)
                }
              />
              <span>
                <strong>Accept remote image uploads</strong>
              </span>
            </label>
            {formRemoteUploadEnabled && (
              <>
                <label htmlFor="remote-upload-directory">Receive directory:</label>
                <select
                  id="remote-upload-directory"
                  value={formRemoteUploadDir}
                  onChange={(event) => setFormRemoteUploadDir(event.target.value)}
                  className="file-path-input"
                >
                  <option value="">Select an image directory</option>
                  {formImageDirs.map((directory) => (
                    <option key={directory} value={directory}>
                      {directory}
                    </option>
                  ))}
                </select>
                <label htmlFor="remote-upload-placement">Folder layout:</label>
                <select
                  id="remote-upload-placement"
                  value={formRemoteUploadPlacement}
                  onChange={(event) =>
                    setFormRemoteUploadPlacement(
                      event.target.value as RemoteImageUploadPlacement
                    )
                  }
                  className="file-path-input"
                >
                  <option value="flat">Receive directory</option>
                  <option value="target_tree">Match catalog</option>
                </select>
                {formRemoteUploadPlacement === 'target_tree' && (
                  <>
                    {formRemoteUploadCatalogMatch && (
                      <small className="remote-upload-token-notice" role="status">
                        Detected from {formRemoteUploadCatalogMatch.samples}{' '}
                        catalog {formRemoteUploadCatalogMatch.samples === 1
                          ? 'image'
                          : 'images'}:{' '}
                        <code>{formRemoteUploadCatalogMatch.template}</code>
                      </small>
                    )}
                    <label htmlFor="remote-upload-directory-template">
                      Layout:
                    </label>
                    <div className="file-input-group">
                      <select
                        id="remote-upload-directory-template"
                        value={
                          formRemoteUploadLayoutChoice === 'catalog' &&
                          formRemoteUploadCatalogMatch
                            ? 'catalog'
                            : isRemoteUploadDirectoryTemplatePreset(
                                  formRemoteUploadDirectoryTemplate
                                )
                              ? formRemoteUploadDirectoryTemplate
                              : 'custom'
                        }
                        onChange={(event) => {
                          const choice = event.target.value;
                          setFormRemoteUploadLayoutTouched(true);
                          if (choice === 'catalog') {
                            setFormRemoteUploadLayoutChoice('catalog');
                            return;
                          }
                          setFormRemoteUploadLayoutChoice('preset');
    setFormRemoteUploadLayoutTouched(false);
                          setFormRemoteUploadDirectoryTemplate(
                            choice === 'custom' ? '' : choice
                          );
                          // A queued rescan would find the catalog match again;
                          // the person just chose something else.
                          setFormRemoteUploadRescanRequested(false);
                        }}
                        className="file-path-input"
                      >
                        {formRemoteUploadCatalogMatch && (
                          <option value="catalog">
                            Match catalog: {formRemoteUploadCatalogMatch.template}
                          </option>
                        )}
                        {REMOTE_UPLOAD_DIRECTORY_TEMPLATE_PRESETS.map((template) => (
                          <option key={template} value={template}>
                            {template}
                          </option>
                        ))}
                        <option value="custom">Custom</option>
                      </select>
                      <button
                        type="button"
                        className="browse-button"
                        aria-label="Rescan catalog layout"
                        aria-pressed={formRemoteUploadRescanRequested}
                        title={
                          formRemoteUploadRescanRequested
                            ? 'Catalog layout rescan queued for Save Changes'
                            : 'Rescan catalog paths when changes are saved'
                        }
                        disabled={formRemoteUploadRescanRequested}
                        onClick={() => setFormRemoteUploadRescanRequested(true)}
                      >
                        ↻ {formRemoteUploadRescanRequested ? 'Rescan queued' : 'Rescan'}
                      </button>
                    </div>
                    {formRemoteUploadLayoutChoice === 'preset' &&
                      !isRemoteUploadDirectoryTemplatePreset(
                        formRemoteUploadDirectoryTemplate
                      ) && (
                      <input
                        type="text"
                        aria-label="Custom catalog layout:"
                        className="file-path-input"
                        value={formRemoteUploadDirectoryTemplate}
                        onChange={(event) => {
                          setFormRemoteUploadLayoutTouched(true);
                          setFormRemoteUploadDirectoryTemplate(event.target.value);
                        }}
                        placeholder="%TARGET%/%NIGHT%/%TYPE%"
                        maxLength={240}
                      />
                    )}
                  </>
                )}
              </>
            )}
            <label htmlFor="server-export-directory">
              Server export directory:
            </label>
            <PathField
              id="server-export-directory"
              className="file-path-input"
              dialogTitle="Select the export directory"
              placeholder="Absolute server path; empty disables server export"
              title="Save Library exports in this folder. Leave blank to offer an archive download instead."
              value={formExportDir}
              onChange={setFormExportDir}
            />
            <label htmlFor="process-directory">
              Process directory:
            </label>
            <PathField
              id="process-directory"
              className="file-path-input"
              dialogTitle="Select the process directory"
              placeholder="Absolute server path, such as the rig's _Process folder"
              title="Save finished work for this rig here. WBPP can save masters in a master/ subfolder within each processing project folder."
              value={formProcessDir}
              onChange={setFormProcessDir}
            />
            <ScanRulesFields value={formScanRules} onChange={setFormScanRules} />
            <AutoImportFields value={formAutoImport} onChange={setFormAutoImport} />
          </div>
        )}

        {createMode && (
          <label className="quality-analysis-option">
            <input
              type="checkbox"
              checked={createAnalyzeQuality}
              onChange={(event) => setCreateAnalyzeQuality(event.target.checked)}
            />
            <span>
              <strong>Queue background quality analysis</strong>
              <small>
                Reads every image to measure stars, background, clouds, obstructions,
                and pointing. This can take a long time.
                You can run it later from this database&apos;s settings.
              </small>
            </span>
          </label>
        )}

        <div className="modal-buttons">
          <button
            onClick={resetForm}
            className="cancel-button"
            disabled={isApplying}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveForm}
            className="save-button"
            disabled={
              (createMode || pluginImport ? formImageDirs.length === 0 : !formDbPath.trim() || formImageDirs.length === 0) || isApplying || (!editingId && !createMode && tsImportRunning)
            }
          >
            {pluginImport
              ? 'Create catalog for plugin'
              : createMode
                ? 'Create & Import'
              : editingId
                ? 'Save Changes'
                : connectImportedSource ? 'Import from Target Scheduler' : 'Import catalog copy'}
          </button>
        </div>
      </section>
    );
  };

  const importPreviewPanel =
    importDbId && importProgress && importProgress.stage !== '' ? (
      <div className="import-progress-panel">
        {confirmImport && importDbId === confirmImport.id && (
          <div className="import-scope-controls">
            <label className="import-scope-choice">
              Import
              <select
                value={importScope}
                onChange={(event) => setImportScope(event.target.value as ImportScope)}
                disabled={importRunning || isApplying}
              >
                <option value="all">Lights and calibration</option>
                <option value="lights">Lights only</option>
                <option value="calibration">Calibration only</option>
              </select>
            </label>
            {importFolders.length > 0 && (
              <div className="import-folder-tree">
                {importFolders.map((root) => (
                  <details key={root.path} className="import-folder-root">
                    <summary>
                      <label onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={importSelectedDirs.includes(root.path)}
                          onChange={() =>
                            setImportSelectedDirs((current) =>
                              current.includes(root.path)
                                ? current.filter((d) => d !== root.path)
                                : [...current, root.path]
                            )
                          }
                        />
                        {root.path}
                      </label>
                    </summary>
                    {root.children.map((child) => (
                      <div key={child.path} className="import-folder-child">
                        <label>
                          <input
                            type="checkbox"
                            checked={
                              importSelectedDirs.includes(child.path) ||
                              importSelectedDirs.includes(root.path)
                            }
                            disabled={importSelectedDirs.includes(root.path)}
                            onChange={() =>
                              setImportSelectedDirs((current) =>
                                current.includes(child.path)
                                  ? current.filter((d) => d !== child.path)
                                  : [...current, child.path]
                              )
                            }
                          />
                          {child.name}
                        </label>
                        {child.children.length > 0 && (
                          <div className="import-folder-grandchildren">
                            {child.children.map((grandchild) => (
                              <label key={grandchild.path}>
                                <input
                                  type="checkbox"
                                  checked={
                                    importSelectedDirs.includes(grandchild.path) ||
                                    importSelectedDirs.includes(child.path) ||
                                    importSelectedDirs.includes(root.path)
                                  }
                                  disabled={
                                    importSelectedDirs.includes(child.path) ||
                                    importSelectedDirs.includes(root.path)
                                  }
                                  onChange={() =>
                                    setImportSelectedDirs((current) =>
                                      current.includes(grandchild.path)
                                        ? current.filter((d) => d !== grandchild.path)
                                        : [...current, grandchild.path]
                                    )
                                  }
                                />
                                {grandchild.name}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </details>
                ))}
              </div>
            )}
            {(() => {
              // A review picks the folders a run held, often deeper than the
              // tree above lists; they stay visible here and can be unticked.
              const listed = new Set<string>([...confirmImport.image_dirs]);
              for (const root of importFolders) {
                listed.add(root.path);
                for (const child of root.children) {
                  listed.add(child.path);
                  for (const grandchild of child.children) listed.add(grandchild.path);
                }
              }
              const picked = importSelectedDirs.filter((dir) => !listed.has(dir));
              if (picked.length === 0) return null;
              return (
                <div className="import-folder-picked">
                  {picked.map((dir) => (
                    <label key={dir}>
                      <input
                        type="checkbox"
                        checked
                        onChange={() =>
                          setImportSelectedDirs((current) => current.filter((d) => d !== dir))
                        }
                        disabled={importRunning || isApplying}
                      />
                      {dir}
                    </label>
                  ))}
                </div>
              );
            })()}
            <label className="quality-analysis-option import-skip-processed">
              <input
                type="checkbox"
                checked={importSkipProcessed}
                onChange={(event) => setImportSkipProcessed(event.target.checked)}
                disabled={importRunning || isApplying}
              />
              <span>
                <small>
                  Skip processed files. Skip integration masters. When pairing
                  is off, also skip calibrated and registered copies. Use this
                  when the folder contains processing results as well as raw
                  images.
                </small>
              </span>
            </label>
            <label className="quality-analysis-option import-accept-other-rigs">
              <input
                type="checkbox"
                checked={importAcceptOtherRigs}
                onChange={(event) => setImportAcceptOtherRigs(event.target.checked)}
                disabled={importRunning || isApplying}
              />
              <span>
                <small>
                  Include frames from other rigs. PSF Guard normally lists lights
                  from an unknown telescope without importing them. If telescope
                  headers are missing, it checks the camera instead. Turn this on
                  when you change telescopes.
                </small>
              </span>
            </label>
            <button
              className="browse-button"
              onClick={handleRepreviewImport}
              disabled={importRunning || isApplying}
              title="Re-run the preview with the selection above; nothing is written."
            >
              Update preview
            </button>
          </div>
        )}
        <div className="import-progress-line">
          {importRunning && <span className="import-spinner">⏳ </span>}
          {describeImportProgress(importProgress)}
        </div>
        {importProgress.stage === 'complete' && importProgress.outcome && (
          <>
            {(importProgress.outcome.skipped_processed ?? 0) > 0 && (
              <div className="muted">
                {importProgress.outcome.skipped_processed} processed file(s) skipped.
              </div>
            )}
            {(importProgress.outcome.skipped_out_of_scope ?? 0) > 0 && (
              <div className="muted">
                {importProgress.outcome.skipped_out_of_scope} frame(s) excluded by the
                selected frame type.
              </div>
            )}
            {scanRuleNotes(importProgress.outcome).length > 0 && (
              <div className="import-rule-notes">
                {scanRuleNotes(importProgress.outcome).join(' · ')}
              </div>
            )}
            {importProgress.outcome.templates_created > 0 && (
              <div className="import-rule-notes">
                Adds {importProgress.outcome.templates_created} exposure template(s)
              </div>
            )}
            {(importProgress.outcome.other_rigs ?? []).map((other) => (
              <div className="import-other-rig" key={other.rig}>
                {other.frames} frame(s) from another rig left out — {other.rig}
                {' '}(e.g. {other.example}). If that rig belongs in this
                catalog, check <em>Include frames from other rigs</em> and
                update the preview.
              </div>
            ))}
            {importProgress.outcome.attach_summaries.length > 0 && (
              <ul className="import-project-list">
                {importProgress.outcome.attach_summaries.map((a) => (
                  <li key={`${a.project}:${a.target}`}>
                    ↳ existing {a.project} / {a.target} — +{a.frames} frame(s) (
                    {a.matched_by} match)
                    {planChanges(a).map((change) => ` · ${change}`).join('')}
                  </li>
                ))}
              </ul>
            )}
            {importProgress.outcome.project_summaries.length > 0 && (
              <ul className="import-project-list">
                {importProgress.outcome.project_summaries.map((p) => (
                  <li key={p.name}>
                    NEW {p.name} — {p.targets} target(s), {p.frames} frame(s)
                  </li>
                ))}
              </ul>
            )}
            {importProgress.outcome.dry_run &&
              confirmImport &&
              importDbId === confirmImport.id && (
                <div className="modal-buttons import-confirm-buttons">
                  {importProgress.outcome.imported +
                    (importProgress.outcome.renamed ?? 0) +
                    importProgress.outcome.calibration.imported +
                    importProgress.outcome.calibration.updated >
                  0 ? (
                    <div className="import-confirm-content">
                      {importProgress.outcome.imported > 0 && <label className="quality-analysis-option">
                        <input
                          type="checkbox"
                          checked={importAnalyzeQuality}
                          onChange={(event) =>
                            setImportAnalyzeQuality(event.target.checked)
                          }
                        />
                        <span>
                          <strong>Queue background quality analysis</strong>
                          <small>
                            Reads every image to measure stars, background, clouds,
                            obstructions, and pointing. This can take a long time,
                            especially in a debug build. You can run it later from this
                            database&apos;s settings.
                          </small>
                        </span>
                      </label>}
                      <div className="modal-buttons import-action-buttons">
                      <button
                        className="save-button"
                        onClick={handleConfirmImport}
                        disabled={isApplying}
                      >
                        Import{' '}
                        {importProgress.outcome.imported +
                          (importProgress.outcome.renamed ?? 0) +
                          importProgress.outcome.calibration.imported +
                          importProgress.outcome.calibration.updated}{' '}
                        frame(s)
                      </button>
                      <button
                        className="cancel-button"
                        onClick={() => {
                          setConfirmImport(null);
                          setStatusMessage('Import cancelled — nothing was written.');
                        }}
                        disabled={isApplying}
                      >
                        Cancel
                      </button>
                      </div>
                    </div>
                  ) : (
                    <span className="muted">
                      Nothing new to import — every frame is already in the database.
                    </span>
                  )}
                </div>
              )}
          </>
        )}
      </div>
    ) : null;

  return (
    <div className="tauri-settings modal-overlay" onClick={requestClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>PSF Guard Settings</h2>
          <button className="close-button" aria-label="Close settings" onClick={requestClose}>×</button>
        </div>

        <div
          className="settings-tabs"
          role="tablist"
          aria-label="Settings sections"
          onKeyDown={onTabKeyDown}
        >
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`settings-tab-${tab.id}`}
              aria-selected={tab.id === currentTab}
              aria-controls={`settings-panel-${tab.id}`}
              tabIndex={tab.id === currentTab ? 0 : -1}
              className="settings-tab"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="modal-body"
          role="tabpanel"
          id={`settings-panel-${currentTab}`}
          aria-labelledby={`settings-tab-${currentTab}`}
        >
          {currentTab === 'review' && <div className="settings-setups"><ReviewPreferences /></div>}
          {currentTab === 'databases' && <>{tsJobs.data?.filter(job => job.running || job.error).map(job => <p key={job.id} role={job.error ? 'alert' : 'status'}>{job.name || 'Target Scheduler'}: {job.error || (job.stage === 'copying' ? `Copying database · ${job.pages_done}/${job.pages_total} pages` : job.stage === 'initializing' ? 'Preparing the working catalog…' : 'Syncing captures and grades…')}</p>)}</>}
          {currentTab === 'databases' && (
          <div className="settings-setups">
          {!hasDatabases && managementAllowed && (
            <div className="welcome-message">
              <h3>🚀 Welcome to PSF Guard!</h3>
              {!showAddForm && <p>
                Start from folders of FITS or XISF images, or from a N.I.N.A.
                scheduler database you already have. You can add more catalogs
                later.
              </p>}
              {!showAddForm && (
                <div className="welcome-choices">
                  <button
                    className="add-directory-button"
                    onClick={startCreate}
                    disabled={isApplying}
                  >
                    <span className="welcome-choice-title">
                      ✨ New Database from Images
                    </span>
                    <span className="welcome-choice-detail">
                      Pick folders of FITS or XISF files. PSF Guard builds a
                      Target Scheduler database and imports them.
                    </span>
                  </button>
                  <button
                    className="add-directory-button"
                    onClick={startAdd}
                    disabled={isApplying}
                  >
                    <span className="welcome-choice-title">
                      + Import from Target Scheduler
                    </span>
                    <span className="welcome-choice-detail">
                      Import a PSF Guard-owned copy of your Target Scheduler database and keep the source connected for sync.
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}

          {!managementAllowed && (
            <div className="welcome-message is-warning">
              <h3>🔒 Database management is read-only</h3>
              <p>
                This server was launched without
                <code style={{ margin: '0 4px' }}>--allow-database-management</code>,
                so the configured database list cannot be changed from the
                browser. Add databases on the command line —
                <code style={{ margin: '0 4px' }}>
                  psf-guard server &lt;db&gt; &lt;image-dirs…&gt;
                </code>
                — or restart the server with the flag to enable add/edit/remove
                here.
              </p>
            </div>
          )}

          {(hasDatabases || isLoading || importPreviewPanel) && <div className="settings-section">
            <h3>Configured Databases {hasDatabases && <span className="muted">({databases.length})</span>}</h3>

            {isLoading && <div className="detecting-database">Loading…</div>}

            {!isLoading && databases.length === 0 && !showAddForm && (
              <div className="no-directories">
                No databases configured yet.
              </div>
            )}

            {databases.map((entry) => {
              const isEditing = showAddForm && editingId === entry.id;
              const editorId = `database-editor-${entry.id}`;

              return (
                <section
                  key={entry.id}
                  className={`db-entry${isEditing ? ' editing' : ''}`}
                  aria-labelledby={`database-heading-${entry.id}`}
                >
                  <div className="db-row">
                    <div className="db-row-head">
                      <h4 className="db-row-title">
                        <DatabaseIcon size={18} aria-hidden="true" />
                        <strong id={`database-heading-${entry.id}`}>{entry.name}</strong>
                        <code className="db-row-slug">{entry.id}</code>
                      </h4>
                      {canEdit && (
                        <div className="db-row-actions">
                          <button
                            type="button"
                            className="db-action-button is-primary"
                            onClick={() => handleImport(entry)}
                            disabled={isApplying || (importRunning && importDbId === entry.id)}
                            title="Scan this database's image directories and import new frames"
                          >
                            <Download size={15} aria-hidden="true" />
                            {importRunning && importDbId === entry.id ? 'Importing…' : 'Import'}
                          </button>
                          <PurgeRejectsButton dbId={entry.id} dbName={entry.name} canManage={canEdit} disabled={isApplying} />
                          {/* Editing a database names its folders: the gate. */}
                          {managementAllowed && (
                            <>
                              <button
                                type="button"
                                className="db-action-button"
                                onClick={() => startEdit(entry)}
                                disabled={isApplying}
                                aria-label={`Edit ${entry.name}`}
                                aria-expanded={isEditing}
                                aria-controls={isEditing ? editorId : undefined}
                              >
                                <Pencil size={14} aria-hidden="true" />
                                Edit
                              </button>
                              <button
                                type="button"
                                className="db-action-button is-icon is-danger"
                                onClick={() => handleRemove(entry)}
                                disabled={isApplying}
                                aria-label={`Remove ${entry.name}`}
                                title="Remove this database from PSF Guard; its files stay on disk"
                              >
                                <Trash2 size={15} aria-hidden="true" />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <dl className="db-row-paths">
                      <div>
                        <dt>{entry.ts_sync ? 'PSF Guard catalog' : 'Catalog'}</dt>
                        <dd>{entry.db_path}</dd>
                      </div>
                      {entry.image_dirs.length > 0 && (
                        <div>
                          <dt>{entry.image_dirs.length === 1 ? 'Images' : 'Image folders'}</dt>
                          <dd>
                            {entry.image_dirs.map((dir, index) => <span key={index}>{dir}</span>)}
                          </dd>
                        </div>
                      )}
                      {entry.remote_image_upload?.enabled && (
                        <div>
                          <dt>Remote receive</dt>
                          <dd>
                            {entry.remote_image_upload.image_dir} (
                            {entry.remote_image_upload.placement === 'target_tree'
                              ? entry.remote_image_upload.directory_template_source ===
                                'catalog'
                                ? `match catalog, ${entry.remote_image_upload.directory_template_samples ?? 0} ${entry.remote_image_upload.directory_template_samples === 1 ? 'sample' : 'samples'}`
                                : 'match catalog'
                              : 'receive directory'}
                            )
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="db-row-details">
                      <CalibrationLibrarySummary
                        dbId={entry.id}
                        onOpen={onClose}
                      />
                      <AstroBinFilterSummary
                        dbId={entry.id}
                        dbName={entry.name}
                        canManage={canEdit}
                      />
                      <MissingGuidRepair dbId={entry.id} dbName={entry.name} canManage={canEdit} />
                      <RenamedLightsRepair dbId={entry.id} dbName={entry.name} canManage={canEdit} />
                      <QualityBackfillControls dbId={entry.id} canManage={managementAllowed} />
                      <CalibratedCopiesControls dbId={entry.id} canManage={managementAllowed} />
                      <AutoImportSummary
                        dbId={entry.id}
                        settings={entry.autoimport}
                        onReview={
                          canEdit
                            ? (folders) => handleImport(entry, folders)
                            : undefined
                        }
                      />
                    </div>
                  </div>
                  {isEditing && <div id={editorId}>{renderDatabaseForm(entry)}</div>}
                  {managementAllowed && entry.ts_sync && <TsSyncSettings dbId={entry.id} settings={entry.ts_sync} onSaved={reload} />}
                  {importDbId === entry.id && importPreviewPanel && (
                    <div ref={importPreviewRef}>{importPreviewPanel}</div>
                  )}
                </section>
              );
            })}

            {/* With no databases yet the welcome banner carries these same two
                actions, so rendering them here too would just duplicate them. */}
            {managementAllowed && hasDatabases && !showAddForm && (
              <div className="db-add-buttons">
                <button
                  type="button"
                  className="db-action-button"
                  onClick={startAdd}
                  disabled={isApplying}
                  title="Import a PSF Guard catalog and keep Target Scheduler connected for sync"
                >
                  <Plus size={15} aria-hidden="true" />
                  Import from Target Scheduler
                </button>
                <button
                  type="button"
                  className="db-action-button"
                  onClick={startCreate}
                  disabled={isApplying}
                  title="Create a brand-new Target Scheduler database and import folders of images into it"
                >
                  <Sparkles size={15} aria-hidden="true" />
                  New Database from Images
                </button>
              </div>
            )}

            {/* An import whose database is not in the list above (one just
                created, before the list refreshes) still shows here. */}
            {importPreviewPanel && !databases.some((entry) => entry.id === importDbId) && (
              <div ref={importPreviewRef}>{importPreviewPanel}</div>
            )}
          </div>}

          {managementAllowed && showAddForm && !editingId && renderDatabaseForm(null)}
          </div>
          )}

          {currentTab === 'catalogs' && (
            <div className="settings-setups">
              <SeizaCatalogControls focus={initialIntent === 'catalogs' ? initialField : null} />
            </div>
          )}

          {/* Planning's panels keep Planning's controls; the container adds no padding. */}
          {currentTab === 'rigs' && <div className="settings-setups director-page director-embedded"><DirectorRigs /></div>}

          {currentTab === 'templates' && <div className="settings-setups director-page director-embedded"><TemplateLibrary /></div>}

          {currentTab === 'sync' && (
            <div className="settings-setups">
              <SchedulerSyncControls databases={databases} disabled={isApplying} />
              <RemoteSyncPreviews databases={databases} disabled={isApplying} />
              <RemotePeerSync databases={databases} disabled={isApplying} />
            </div>
          )}

          {currentTab === 'setups' && (
            <div className="settings-setups">
              <ProcessingSetupsManager />
              <ExportDefaultsSettings />
              <PixInsightSettings />
              <AstroBinSettings />
            </div>
          )}

          {currentTab === 'calibration' && (
            <div className="settings-setups">
              <CalibrationMatchingSettings focusField={initialRig ? null : initialField} />
              <CalibrationRigSettings focusRig={initialRig} focusField={initialRig ? initialField : null} />
            </div>
          )}

          {currentTab === 'stacking' && (
            <div className="settings-setups">
              <StackMethodSettings />
              <StackAutomationSettings />
            </div>
          )}

          {currentTab === 'performance' && (
            <div className="settings-setups">
              <WorkerShareSettings />
            </div>
          )}

          {currentTab === 'storage' && (
            <div className="settings-setups">
              <StorageSettings canManage={managementAllowed} />
            </div>
          )}

          {currentTab === 'backups' && <BackupSettings />}

          {currentTab === 'server' && <div className="settings-setups"><DesktopServerSettings /></div>}
          {currentTab === 'agents' && (
            <div className="settings-setups">
              <AgentsSettings onServerSettings={isTauri ? () => setActiveTab('server') : undefined} />
            </div>
          )}

          {currentTab === 'users' && (
            <div className="settings-setups">
              <UserManagement currentUsername={access.status.username} />
              <ApiTokenManagement
                currentUsername={access.status.username}
                isEditor={access.status.role === 'read_write'}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          {statusMessage && (
            <div
              className={`save-message ${
                statusMessage.includes('Failed') || statusMessage.includes('failed')
                  ? 'error'
                  : 'success'
              }`}
            >
              {statusMessage}
            </div>
          )}
          {confirmClose && unsaved.length > 0 && (
            <div className="unsaved-confirm" role="alertdialog" aria-labelledby="unsaved-confirm-text">
              <p id="unsaved-confirm-text">
                Unsaved changes in {describeUnsaved(unsaved)}. Close without saving them?
              </p>
              {/* Focus lands on the safe choice. */}
              <button type="button" autoFocus onClick={() => setConfirmClose(false)}>
                Keep editing
              </button>
              <button
                type="button"
                className="unsaved-discard"
                onClick={() => { setConfirmClose(false); onClose(); }}
              >
                Close without saving
              </button>
            </div>
          )}
          <div className="modal-buttons">
            <button
              onClick={requestClose}
              className="save-button"
              disabled={isApplying}
              title={unsaved.length > 0 ? `Unsaved changes in ${describeUnsaved(unsaved)}` : undefined}
            >
              Done
            </button>
            {isTauri && <OpenLogsButton onError={setStatusMessage} />}
            {currentTab !== 'server' && <button
              onClick={handleRestart}
              className="cancel-button"
              disabled={isApplying}
              title={
                isTauri
                  ? 'Force a server restart (rarely needed — changes are applied live)'
                  : 'Reload the page'
              }
            >
              {isTauri ? 'Restart Server' : 'Reload Page'}
            </button>}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Agents on a server without accounts: the endpoint answers this machine
 *  without a token, so the setup is the URL alone. */
function AgentsSettings({ onServerSettings }: { onServerSettings?: () => void }) {
  const url = useMcpUrl();
  return (
    <div className="settings-section">
      <h3>Agents</h3>
      <p>
        Claude Code, Codex or another MCP client can read the catalog, grade and start jobs
        through <code>{url || '/api/mcp'}</code>. This server has no accounts, so it answers
        only this machine and needs no token.
      </p>
      <AgentConnect url={url} />
      {onServerSettings && <p><button type="button" className="link-button" onClick={onServerSettings}>
        Choose a fixed server port
      </button> to keep this address across desktop restarts.</p>}
    </div>
  );
}
